/**
 * Lightweight link classification used across the /groups module.
 * The actual smooth-scroll + URL-cleanup behaviour for hash links is wired
 * up globally by `NavigatorScript`, so components only need to know whether
 * a link goes off-site (to render `target="_blank"` + `rel="noopener"`).
 */

export type LinkKind = "hash" | "external" | "internal" | "none";

const ABSOLUTE_HTTP_RE = /^https?:\/\//i;

/** Whether a value is a non-empty href fragment (`#…`, with content). */
export function isHashLink(href: string | null | undefined): boolean {
  if (!href) return false;
  const v = href.trim();
  return v.startsWith("#") && v.length > 1 && v !== "#!";
}

/** Whether the href points to an absolute HTTP(S) URL on another origin. */
export function isExternalLink(href: string | null | undefined): boolean {
  if (!href) return false;
  return ABSOLUTE_HTTP_RE.test(href.trim());
}

/**
 * Categorise an href for rendering decisions. We only need to differentiate
 * external (open in a new tab) from everything else — hash links are
 * intercepted globally by `NavigatorScript`, and internal links flow through
 * the browser's normal navigation.
 */
export function classifyLink(href: string | null | undefined): LinkKind {
  const v = href?.trim();
  if (!v) return "none";
  if (v.startsWith("#")) return v === "#" ? "none" : "hash";
  if (ABSOLUTE_HTTP_RE.test(v)) return "external";
  return "internal";
}
